/**
 * Storefront cart actions for the embedded widget.
 *
 * The widget runs inside merchant Shopify pages (ampbeauty.com), so it can
 * call the storefront's own AJAX cart endpoints — same-origin to the
 * embedding document. No CORS, no auth tokens, no Storefront Access Token
 * dance. That's the whole reason this is small.
 *
 * Reference: https://shopify.dev/api/ajax/reference/cart
 *
 * Flow when a shopper clicks "Add to bag" on a product card in chat:
 *
 *   1. Fetch /products/<handle>.js to get the product's variants.
 *      We need a variant_id to add to cart; the AI agent only carries
 *      product handles, not variant IDs (catalog DB doesn't sync variants).
 *      One-shot fetch at click-time keeps the widget simple and the
 *      server-side schema unchanged.
 *
 *   2. Pick a variant. Most AMP products are single-variant (one size /
 *      one shade). Multi-variant products show up too (e.g., shade options).
 *      For v1 we pick the FIRST AVAILABLE variant — that's what Shopify's
 *      own "Quick Add" pattern does for product grids. If a shopper wants
 *      a specific shade, the PDP is one click away.
 *
 *   3. POST /cart/add.js with { id: variant_id, quantity: 1 }.
 *
 *   4. Tell the theme to open its cart drawer. Different themes listen for
 *      different events, so we dispatch ALL the common patterns. If none
 *      hook, we still added the item — the next page navigation or cart
 *      page visit will reflect it. Worst case the shopper doesn't see the
 *      animation, not a broken state.
 *
 * Returns the cart-add response (the item shape) or throws on failure.
 */

interface ShopifyVariant {
  id: number;
  available: boolean;
  price: number;
  title: string;
}

interface ShopifyProductJson {
  id: number;
  handle: string;
  title: string;
  variants: ShopifyVariant[];
}

export interface CartAddResult {
  id: number;
  title: string;
  quantity: number;
  variant_title: string | null;
  product_title: string;
  image: string | null;
  price: number;
}

export class CartAddError extends Error {
  constructor(
    message: string,
    public readonly stage: "fetch-product" | "pick-variant" | "add-to-cart" | "parse",
    public readonly status?: number
  ) {
    super(message);
    this.name = "CartAddError";
  }
}

/**
 * Add a product to the cart by handle. Resolves to the cart line item on
 * success. Throws CartAddError with a `stage` discriminator on failure so
 * the caller can show a useful message.
 */
export async function addToCartByHandle(handle: string): Promise<CartAddResult> {
  // 1. Fetch product data to find a variant id. Shopify exposes this at
  //    /products/<handle>.js on every storefront — no auth, same-origin.
  let product: ShopifyProductJson;
  try {
    const resp = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!resp.ok) {
      throw new CartAddError(
        `Couldn't load product details (HTTP ${resp.status})`,
        "fetch-product",
        resp.status
      );
    }
    product = (await resp.json()) as ShopifyProductJson;
  } catch (err) {
    if (err instanceof CartAddError) throw err;
    throw new CartAddError(
      `Couldn't load product: ${err instanceof Error ? err.message : "unknown"}`,
      "fetch-product"
    );
  }

  // 2. Pick a variant: first available, else first listed. Shopify still
  //    accepts an unavailable variant id (returns 422), so we'd rather
  //    show "Add to bag failed" than silently add an out-of-stock variant.
  const variants = product.variants ?? [];
  const variant = variants.find((v) => v.available) ?? variants[0];
  if (!variant) {
    throw new CartAddError("This product has no purchasable variants.", "pick-variant");
  }

  // 3. Add to cart. Shopify's /cart/add.js accepts JSON or form-encoded;
  //    JSON keeps types clean.
  let added: CartAddResult;
  try {
    const resp = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({ id: variant.id, quantity: 1 }),
    });
    if (!resp.ok) {
      // 422 typically means out of stock / variant inactive. Parse the
      // error description if Shopify gave one.
      let description = `HTTP ${resp.status}`;
      try {
        const errBody = (await resp.json()) as { description?: string; message?: string };
        description = errBody.description ?? errBody.message ?? description;
      } catch {
        // ignore
      }
      throw new CartAddError(description, "add-to-cart", resp.status);
    }
    added = (await resp.json()) as CartAddResult;
  } catch (err) {
    if (err instanceof CartAddError) throw err;
    throw new CartAddError(
      `Couldn't add to bag: ${err instanceof Error ? err.message : "unknown"}`,
      "add-to-cart"
    );
  }

  // 4. Nudge the theme to open its cart drawer. Different themes use
  //    different conventions; we fire a bouquet so at least one lands.
  //    Even if none do, the cart is still updated server-side — the
  //    shopper can click the cart icon to see the item.
  if (typeof window !== "undefined") {
    const eventDetail = { item: added };
    // Names harvested from the live storefront's theme.min.js. Stiletto
    // (the Fluorescent theme AMP Beauty uses) listens for cart:item-added
    // and cart:updated specifically — those refresh the cart-icon count
    // and open the drawer. The rest are defense-in-depth for other themes
    // we might support later.
    const events = [
      // Stiletto / Fluorescent — confirmed via theme.min.js grep
      "cart:item-added",
      "cart:updated",
      "cart:refresh",
      "cart:open",
      "ajaxCart.afterCartLoad",
      // Dawn-family (Shopify reference theme) events
      "cart-drawer:open",
      // Generic catch-alls some themes listen for
      "cart-added",
      "ajax-cart:add-success",
    ];
    for (const name of events) {
      try {
        document.dispatchEvent(new CustomEvent(name, { detail: eventDetail, bubbles: true }));
        window.dispatchEvent(new CustomEvent(name, { detail: eventDetail }));
      } catch {
        // CustomEvent constructor missing in IE11 — irrelevant, we target evergreens.
      }
    }
    // Some themes only respond to a click on their cart drawer trigger.
    // If we find one in the DOM, fire it as a last-resort fallback after
    // a short delay (so the events above have a chance to handle it first).
    setTimeout(() => {
      try {
        const trigger = document.querySelector<HTMLElement>(
          "[data-cart-drawer-trigger], [data-action='open-drawer'][data-drawer='cart'], [aria-controls*='cart-drawer'], .js-drawer-open-cart"
        );
        // Only auto-click if the cart drawer isn't already visible.
        const drawerVisible = document.querySelector<HTMLElement>(
          ".cart-drawer[aria-hidden='false'], .drawer--cart.is-open, .cart-drawer.is-active"
        );
        if (trigger && !drawerVisible) trigger.click();
      } catch {
        // ignore — best-effort nudge
      }
    }, 150);
  }

  return added;
}
